import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useAktion, AKTION_BANNER_DISMISSED_KEY } from "@/lib/useAktion";
import { trackAktion } from "@/lib/analytics";

/**
 * Sticky Aktions-Streifen ganz oben auf der Seite. Erscheint nur wenn
 * `useAktion().active === true` (UTM/Session-Match) UND der Nutzer den
 * Streifen in dieser Session noch nicht weggeklickt hat.
 *
 * Wenn geschlossen → SessionStorage-Flag; Hero-Chip + Aktions-Sektion
 * bleiben weiterhin sichtbar (Anti-Nervig-Regel: Streifen weg, Aktion bleibt).
 */
export default function AktionBanner() {
    const location = useLocation();
    const aktion = useAktion();
    const [dismissed, setDismissed] = useState<boolean>(() => {
        try { return sessionStorage.getItem(AKTION_BANNER_DISMISSED_KEY) === "1"; } catch { return false; }
    });

    useEffect(() => {
        if (!aktion.active || dismissed) return;
        trackAktion("banner_shown");
    }, [aktion.active, dismissed]);

    // Auf `/aktion` niemals den Editorial-Streifen zeigen — die
    // Aktions-Landing hat ihren eigenen, wesentlich lauteren Kontext.
    if (location.pathname === "/aktion") return null;
    if (!aktion.active || dismissed) return null;

    const onCta = () => {
        trackAktion("banner_cta_clicked");
        const el = document.getElementById("aktion-section");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const onClose = () => {
        try { sessionStorage.setItem(AKTION_BANNER_DISMISSED_KEY, "1"); } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div
            className="sticky top-0 z-[70] w-full"
            style={{
                background: "#FFFDF9",
                borderBottom: "1px solid #D8CDBF",
                color: "#1D1B18",
            }}
            data-testid="aktion-banner"
        >
            <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 px-4 md:px-6 py-2.5 min-h-[44px]">
                <div className="flex-1 text-xs md:text-sm truncate">
                    <span
                        className="inline-block align-middle mr-2 uppercase text-[9px] tracking-[0.16em] px-2 py-0.5 rounded-sm"
                        style={{ background: "#A78150", color: "#FFFDF9", letterSpacing: "0.16em" }}
                        aria-hidden
                    >
                        Aktion
                    </span>
                    <span>{aktion.banner_text}</span>
                </div>
                <button
                    type="button"
                    onClick={onCta}
                    className="whitespace-nowrap text-xs md:text-sm px-3 py-1.5 rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A78150]"
                    style={{ background: "#A78150", color: "#FFFDF9", fontWeight: 500 }}
                    data-testid="aktion-banner-cta"
                >
                    {aktion.banner_cta_label}
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Streifen schließen"
                    className="p-1 rounded-sm transition-colors"
                    style={{ color: "#1D1B18", opacity: 0.6 }}
                    data-testid="aktion-banner-close"
                >
                    <X size={14} strokeWidth={2} />
                </button>
            </div>
        </div>
    );
}
